import React, { useState, useEffect, useRef } from 'react';
import { reportsAPI, schoolLogoAPI, authAPI } from '../services/api';
import { useYear } from '../context/YearContext';
import { CalendarDays, Pencil, Check, X } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
} from 'recharts';

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

// Convert school name to URL-friendly slug (must match backend toSlug)
function toSlug(name) {
  return (name || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// Shared breakdown card grid used by Total Collected / Pending / Billed / Today
function BreakdownCards({ rows, total }) {
  if (rows.length === 0) return <p className="no-data">No data yet.</p>;
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      {rows.map((r) => (
        <div key={r.label} style={{
          background: r.bg, borderRadius: 10, padding: '14px 22px',
          minWidth: 160, flex: '1 1 160px',
        }}>
          <div style={{ fontWeight: 700, color: r.color, fontSize: '1rem', marginBottom: 4 }}>{r.label}</div>
          <div style={{ fontWeight: 800, fontSize: '1.35rem', color: r.color }}>{fmt(r.amount)}</div>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 4 }}>
            {total > 0 ? ((r.amount / total) * 100).toFixed(1) : 0}% of total
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Fee type bar chart data ──
function buildChartData(stats) {
  const p = (v) => parseFloat(v || 0);
  const types = [
    { name: 'Tuition', billed: p(stats.billed_tuition), collected: p(stats.collected_tuition), pending: Math.max(0, p(stats.pending_tuition)) },
    { name: 'Bus',     billed: p(stats.billed_bus),     collected: p(stats.collected_bus),     pending: Math.max(0, p(stats.pending_bus))     },
    { name: 'Uniform', billed: p(stats.billed_uniform), collected: p(stats.collected_uniform), pending: Math.max(0, p(stats.pending_uniform)) },
    { name: 'Books',   billed: p(stats.billed_books),   collected: p(stats.collected_books),   pending: Math.max(0, p(stats.pending_books))   },
    { name: 'Hostel',  billed: p(stats.billed_hostel),  collected: p(stats.collected_hostel),  pending: Math.max(0, p(stats.pending_hostel))  },
  ].filter((d) => d.billed > 0 || d.collected > 0);
  return types;
}

// ── Collection rate pie chart data ──
function buildPieData(stats) {
  return [
    { name: 'Collected', value: parseFloat(stats.total_collected || 0), color: '#22c55e' },
    { name: 'Pending',   value: parseFloat(stats.total_pending   || 0), color: '#f97316' },
  ].filter((d) => d.value > 0);
}

// ── Custom chart tooltip ──
const ChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#1e293b', borderRadius: 8, padding: '8px 12px', boxShadow: '0 4px 16px rgba(0,0,0,.3)' }}>
        {label && <p style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</p>}
        {payload.map((entry) => (
          <p key={entry.name} style={{ color: entry.color, fontSize: 12, margin: '2px 0', fontWeight: 600 }}>
            {entry.name}: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};
const LOGO_ACCEPT = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml';
const LOGO_FILE_EXT = /\.(jpe?g|png|gif|webp|svg)$/i;
const MAX_LOGO_SIZE = 15 * 1024 * 1024;

function LogoUploadSection({ hasLogo }) {
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const actionText = hasLogo ? 'Replace Logo' : 'Upload Logo';

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] || null;
    setMessage('');
    setError('');
    setFile(null);

    if (!selected) return;
    if (selected.size > MAX_LOGO_SIZE) {
      setError('Logo file must be 15 MB or smaller.');
      e.target.value = '';
      return;
    }
    if (!LOGO_ACCEPT.split(',').includes(selected.type) && !LOGO_FILE_EXT.test(selected.name || '')) {
      setError('Please select a JPG, JPEG, PNG, GIF, WEBP, or SVG image.');
      e.target.value = '';
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please choose a logo image first.');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await schoolLogoAPI.upload(file);
      const url = schoolLogoAPI.resolveUrl(res.data?.data?.logo_url || '');
      if (url) sessionStorage.setItem('gs_school_logo_url', url);
      window.dispatchEvent(new CustomEvent('school-logo-updated', { detail: { logoUrl: url } }));
      setMessage(hasLogo ? 'Logo replaced successfully.' : 'Logo uploaded successfully.');
      setFile(null);
      e.target.reset();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save logo.');
    } finally {
      setSaving(false);
    }
  };

  const fileInputRef = useRef(null);

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={LOGO_ACCEPT}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          title={hasLogo ? 'Replace school logo' : 'Upload school logo'}
          style={{
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: 6,
            cursor: 'pointer',
            padding: '3px 7px',
            color: '#fff',
            fontSize: '0.78rem',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            lineHeight: 1,
          }}
        >
          🖼️ {file ? file.name.slice(0, 14) + (file.name.length > 14 ? '…' : '') : (hasLogo ? 'Replace logo' : 'Add logo')}
        </button>
        {file && (
          <button type="submit" disabled={saving}
            style={{
              background: '#4f46e5',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              padding: '3px 10px',
              color: '#fff',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}>
            {saving ? '…' : 'Upload'}
          </button>
        )}
      </form>
      {error   && <span style={{ fontSize: '0.7rem', color: '#fca5a5' }}>{error}</span>}
      {message && <span style={{ fontSize: '0.7rem', color: '#86efac' }}>{message}</span>}
    </div>
  );
}

export default function Dashboard({ schoolName, schoolLocation, schoolTagline, contactName, schoolLogoUrl, onContactNameChange }) {
  const { year, changeYear }  = useYear();
  const [editingPrincipal, setEditingPrincipal] = useState(false);
  const [principalDraft,   setPrincipalDraft]   = useState('');
  const [principalSaving,  setPrincipalSaving]  = useState(false);
  const [stats,        setStats]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [lastFetched,  setLastFetched]  = useState(null);
  const [error,        setError]        = useState(null);
  const [pendingList,  setPendingList]  = useState([]);
  const [topDefaulters, setTopDefaulters] = useState([]);
  const [strength,        setStrength]        = useState(null);
  const [showStrength,     setShowStrength]     = useState(false);
  const [showDueList,      setShowDueList]      = useState(false);
  const [showOldDueList,   setShowOldDueList]   = useState(false);
  const [showArchivedList, setShowArchivedList] = useState(false);
  const [showTCList,       setShowTCList]       = useState(false);
  const [showCollected,    setShowCollected]    = useState(false);
  const [showPending,      setShowPending]      = useState(false);
  const [showBilled,       setShowBilled]       = useState(false);
  const [showToday,        setShowToday]        = useState(false);
  const [archivedStudents, setArchivedStudents] = useState([]);

  const yearRef = useRef(year);
  useEffect(() => { yearRef.current = year; }, [year]);

  const savePrincipal = async () => {
    if (!principalDraft.trim()) return;
    setPrincipalSaving(true);
    try {
      await authAPI.updateProfile({ contact_name: principalDraft.trim() });
      sessionStorage.setItem('gs_contact_name', principalDraft.trim());
      if (onContactNameChange) onContactNameChange(principalDraft.trim());
      setEditingPrincipal(false);
    } catch (e) {
      alert('Failed to save: ' + (e?.response?.data?.message || e.message));
    } finally {
      setPrincipalSaving(false);
    }
  };

  const fetchAll = useRef(null);
  fetchAll.current = (isBackground = false) => {
    if (!isBackground) setLoading(true); else setRefreshing(true);
    const yr = yearRef.current;
    Promise.all([
      reportsAPI.getDashboard(yr),
      reportsAPI.getPendingStudents(yr),
      reportsAPI.getStrength(yr),
      reportsAPI.getArchivedStudents(),
    ])
      .then(([dRes, pRes, sRes, aRes]) => {
        setStats(dRes.data.data);
        const pList = pRes.data.data || [];
        setPendingList(pList);
        setTopDefaulters(pList.slice(0, 5));
        setStrength(sRes.data.data);
        setArchivedStudents(aRes.data.data || []);
        setLastFetched(new Date());
        setError(null);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message || 'Failed to load dashboard data';
        console.error('Dashboard load error:', err);
        if (!isBackground) setError(msg);
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  };

  // Initial load + year change
  useEffect(() => { fetchAll.current(false); }, [year]);

  // Refetch silently when the tab regains focus or becomes visible
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') fetchAll.current(true); };
    const onFocus   = () => fetchAll.current(true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Refetch whenever a payment is recorded anywhere in this session
  useEffect(() => {
    const onPayment = () => fetchAll.current(true);
    window.addEventListener('payment-recorded', onPayment);
    return () => window.removeEventListener('payment-recorded', onPayment);
  }, []);

  if (loading) return <div className="loading">Loading dashboard…</div>;
  if (error)   return <div className="error-page">{error}</div>;

  const collectionRate =
    stats.total_fees > 0
      ? ((stats.total_collected / stats.total_fees) * 100).toFixed(1)
      : 0;

  // ── Export Fee Summary to CSV ──
  const exportFeeSummaryCSV = () => {
    const rows = [
      ['Metric', 'Value'],
      ['Academic Year', year],
      ['Total Strength', stats.total_students],
      ['Fully Paid', stats.fully_paid],
      ['With Dues', stats.with_dues],
      ['Total Fee Billed', stats.total_fees],
      ['Total Collected', stats.total_collected],
      ['Total Pending', stats.total_pending],
      ["Today's Collection", stats.today_collected],
      ['Old Due (Prev. Years)', stats.total_old_due || 0],
      ['Archived Students', stats.archived_count || 0],
      ['TC Issued', stats.tc_issued_count || 0],
      ['Collection Rate (%)', collectionRate],
      [],
      ['— Collected Breakdown —'],
      ['Tuition Collected', stats.collected_tuition || 0],
      ['Bus Collected', stats.collected_bus || 0],
      ['Uniform Collected', stats.collected_uniform || 0],
      ['Books Collected', stats.collected_books || 0],
      ['Hostel Collected', stats.collected_hostel || 0],
      [],
      ['— Pending Breakdown —'],
      ['Tuition Pending', stats.pending_tuition || 0],
      ['Bus Pending', stats.pending_bus || 0],
      ['Uniform Pending', stats.pending_uniform || 0],
      ['Books Pending', stats.pending_books || 0],
      ['Hostel Pending', stats.pending_hostel || 0],
      [],
      ['— Billed Breakdown —'],
      ['Tuition Billed', stats.billed_tuition || 0],
      ['Bus Billed', stats.billed_bus || 0],
      ['Uniform Billed', stats.billed_uniform || 0],
      ['Books Billed', stats.billed_books || 0],
      ['Hostel Billed', stats.billed_hostel || 0],
      [],
      ['— Today Breakdown —'],
      ['Tuition Today', stats.today_tuition || 0],
      ['Bus Today', stats.today_bus || 0],
      ['Uniform Today', stats.today_uniform || 0],
      ['Books Today', stats.today_books || 0],
      ['Hostel Today', stats.today_hostel || 0],
      ['Old Due Today', stats.today_old_due || 0],
      ['Other Today', stats.today_other || 0],
    ];

    // Add pending students list if available
    if (pendingList.length > 0) {
      rows.push([], ['— Students With Dues —'], ['Class', 'Student Name', 'Father Name', 'Village', 'Pending Amount']);
      pendingList.forEach((s) => {
        rows.push([`"${s.class}"`, `"${s.student_name}"`, `"${s.father_name || ''}"`, `"${s.village || ''}"`, s.pending_amount]);
      });
    }

    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Fee_Summary_${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">

      {/* ── School Hero Banner ── */}
      <div className="school-hero">
        <div className="school-hero-left">
          <div className="school-hero-info">
            <div className="school-hero-title-row">
              {schoolLogoUrl ? (
                <img
                  src={schoolLogoUrl}
                  alt={`${schoolName || 'School'} Logo`}
                  className="hero-logo"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : null}
              <h1 className="school-hero-name">{schoolName || 'GEETHAM SCHOOL'}</h1>
              <LogoUploadSection hasLogo={!!schoolLogoUrl} />
            </div>
            {schoolLocation ? (
              <p className="school-hero-location">📍 {schoolLocation}</p>
            ) : null}
            {schoolTagline ? (
              <p className="school-hero-tagline">{schoolTagline}</p>
            ) : null}
          </div>
        </div>
        <div className="principal-card">
          <img
            src="/assets/principal.jpg"
            alt="Principal"
            className="principal-photo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="principal-info">
            <p className="principal-title">SCHOOL PRINCIPAL</p>
            {editingPrincipal ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  autoFocus
                  value={principalDraft}
                  onChange={(e) => setPrincipalDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') savePrincipal(); if (e.key === 'Escape') setEditingPrincipal(false); }}
                  style={{ fontSize: '.8rem', fontWeight: 700, background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.4)', borderRadius: 4, color: '#fff', padding: '2px 6px', width: 100 }}
                />
                <button onClick={savePrincipal} disabled={principalSaving} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#fff' }}><Check size={13} /></button>
                <button onClick={() => setEditingPrincipal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#fff' }}><X size={13} /></button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <p className="principal-name" style={{ margin: 0 }}>{contactName || '—'}</p>
                <button
                  onClick={() => { setPrincipalDraft(contactName || ''); setEditingPrincipal(true); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'rgba(255,255,255,.55)', lineHeight: 1 }}
                  title="Edit principal name"
                ><Pencil size={11} /></button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Dashboard</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.65rem' }}>
          <div className="year-pill-wrap">
            <CalendarDays size={15} className="year-icon" />
            <select
              className="year-select-header"
              value={year}
              onChange={(e) => changeYear(e.target.value)}
              title="Academic Year"
            >
              {Array.from({ length: 12 }, (_, i) => 2022 + i).map((y) => {
                const label = `${y}-${String(y + 1).slice(-2)}`;
                return <option key={label} value={label}>{label}</option>;
              })}
            </select>
          </div>
          <div className="header-school-chip">
            <span className="header-school-dot" />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 className="section-title" style={{ marginBottom: 0 }}>Fee Summary</h2>
          {lastFetched && (
            <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
              as of {lastFetched.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button
            className="btn btn-secondary"
            onClick={() => fetchAll.current(true)}
            disabled={refreshing}
            title="Fetch latest data from server"
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <span style={{ display: 'inline-block', animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>🔄</span>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className="btn btn-secondary" onClick={exportFeeSummaryCSV} title="Export all fee summary data to CSV">
            📥 Export to CSV
          </button>
        </div>
      </div>
      <div className="stats-grid" style={{ marginTop: 16 }}>
        <div
          className="stat-card blue"
          style={{ cursor: 'pointer', outline: showStrength ? '2px solid #1565c0' : 'none' }}
          onClick={() => setShowStrength((v) => !v)}
          title="Click to see gender-wise strength"
        >
          <div className="stat-icon-wrap">👨‍🎓</div>
          <div className="stat-info">
            <div className="stat-value">{stats.total_students}</div>
            <div className="stat-label">Total Strength ▾</div>
            {strength && (
              <div style={{ fontSize: '0.75rem', marginTop: 4, color: '#90caf9' }}>
                👦 {strength.overall.male} &nbsp;|&nbsp; 👧 {strength.overall.female}
              </div>
            )}
          </div>
        </div>

        <div className="stat-card green">
          <div className="stat-icon-wrap">✅</div>
          <div className="stat-info">
            <div className="stat-value">{stats.fully_paid}</div>
            <div className="stat-label">Fully Paid</div>
          </div>
        </div>

        <div
          className="stat-card red"
          style={{ cursor: 'pointer', outline: showDueList ? '2px solid #c0392b' : 'none' }}
          onClick={() => { setShowDueList((v) => !v); setShowOldDueList(false); }}
          title="Click to see students with dues"
        >
          <div className="stat-icon-wrap">⏳</div>
          <div className="stat-info">
            <div className="stat-value">{stats.with_dues}</div>
            <div className="stat-label">With Dues ▾</div>
          </div>
        </div>

        <div
          className="stat-card green"
          style={{ cursor: 'pointer', outline: showCollected ? '2px solid #15803d' : 'none' }}
          onClick={() => setShowCollected((v) => !v)}
          title="Click to see fee-type breakdown"
        >
          <div className="stat-icon-wrap">💰</div>
          <div className="stat-info">
            <div className="stat-value">{fmt(stats.total_collected)}</div>
            <div className="stat-label">Total Collected ▾</div>
          </div>
        </div>

        <div
          className="stat-card orange"
          style={{ cursor: 'pointer', outline: showPending ? '2px solid #c2410c' : 'none' }}
          onClick={() => setShowPending((v) => !v)}
          title="Click to see pending breakdown"
        >
          <div className="stat-icon-wrap">🔴</div>
          <div className="stat-info">
            <div className="stat-value">{fmt(stats.total_pending)}</div>
            <div className="stat-label">Total Pending ▾</div>
          </div>
        </div>

        <div
          className="stat-card purple"
          style={{ cursor: 'pointer', outline: showBilled ? '2px solid #7c3aed' : 'none' }}
          onClick={() => setShowBilled((v) => !v)}
          title="Click to see fee billed breakdown"
        >
          <div className="stat-icon-wrap">📋</div>
          <div className="stat-info">
            <div className="stat-value">{fmt(stats.total_fees)}</div>
            <div className="stat-label">Total Fee Billed ▾</div>
          </div>
        </div>

        <div
          className="stat-card teal"
          style={{ cursor: 'pointer', outline: showToday ? '2px solid #0e7490' : 'none' }}
          onClick={() => setShowToday((v) => !v)}
          title="Click to see today's breakdown"
        >
          <div className="stat-icon-wrap">📅</div>
          <div className="stat-info">
            <div className="stat-value">{fmt(stats.today_collected)}</div>
            <div className="stat-label">Today's Collection ▾</div>
          </div>
        </div>

        <div
          className="stat-card orange"
          style={{ cursor: 'pointer', outline: showArchivedList ? '2px solid #e67e22' : 'none' }}
          onClick={() => { setShowArchivedList((v) => !v); setShowTCList(false); }}
          title="Click to see archived students"
        >
          <div className="stat-icon-wrap">🗃️</div>
          <div className="stat-info">
            <div className="stat-value">{stats.archived_count || 0}</div>
            <div className="stat-label">Archived Students ▾</div>
          </div>
        </div>

        <div
          className="stat-card blue"
          style={{ cursor: 'pointer', outline: showTCList ? '2px solid #1565c0' : 'none' }}
          onClick={() => { setShowTCList((v) => !v); setShowArchivedList(false); }}
          title="Click to see TC issued students"
        >
          <div className="stat-icon-wrap">📄</div>
          <div className="stat-info">
            <div className="stat-value">{stats.tc_issued_count || 0}</div>
            <div className="stat-label">TC Issued ▾</div>
          </div>
        </div>

        <div
          className="stat-card red"
          style={{ cursor: 'pointer', outline: showOldDueList ? '2px solid #c0392b' : 'none' }}
          onClick={() => { setShowOldDueList((v) => !v); setShowDueList(false); }}
          title="Click to see students with old due"
        >
          <div className="stat-icon-wrap">💸</div>
          <div className="stat-info">
            <div className="stat-value">{fmt(stats.total_old_due || 0)}</div>
            <div className="stat-label">Old Due (Prev. Years) ▾</div>
          </div>
        </div>
      </div>

      {/* ── Total Collected Breakdown ── */}
      {showCollected && (() => {
        const tuition = parseFloat(stats.collected_tuition || 0);
        const bus     = parseFloat(stats.collected_bus     || 0);
        const uniform = parseFloat(stats.collected_uniform || 0);
        const books   = parseFloat(stats.collected_books   || 0);
        const hostel  = parseFloat(stats.collected_hostel  || 0);
        const total   = parseFloat(stats.total_collected   || 0);
        const rows = [
          { label: '📚 Tuition',  amount: tuition, color: '#1e40af', bg: '#dbeafe' },
          { label: '🚌 Bus',      amount: bus,     color: '#065f46', bg: '#d1fae5' },
          { label: '👕 Uniform',  amount: uniform,  color: '#7c3aed', bg: '#ede9fe' },
          { label: '📖 Books',    amount: books,    color: '#92400e', bg: '#fef3c7' },
          { label: '🏠 Hostel',   amount: hostel,  color: '#0f766e', bg: '#ccfbf1' },
        ].filter((r) => r.amount > 0);
        return (
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 className="card-title">💰 Total Collected — Breakdown</h2>
            <BreakdownCards rows={rows} total={total} />
          </div>
        );
      })()}

      {/* ── Total Pending Breakdown ── */}
      {showPending && (() => {
        const tuition = parseFloat(stats.pending_tuition || 0);
        const bus     = parseFloat(stats.pending_bus     || 0);
        const uniform = parseFloat(stats.pending_uniform || 0);
        const books   = parseFloat(stats.pending_books   || 0);
        const hostel  = parseFloat(stats.pending_hostel  || 0);
        const total   = parseFloat(stats.total_pending   || 0);
        const rows = [
          { label: '📚 Tuition',  amount: tuition, color: '#b91c1c', bg: '#fee2e2' },
          { label: '🚌 Bus',      amount: bus,     color: '#c2410c', bg: '#ffedd5' },
          { label: '👕 Uniform',  amount: uniform,  color: '#7c3aed', bg: '#ede9fe' },
          { label: '📖 Books',    amount: books,    color: '#92400e', bg: '#fef3c7' },
          { label: '🏠 Hostel',   amount: hostel,  color: '#0f766e', bg: '#ccfbf1' },
        ].filter((r) => r.amount > 0);
        return (
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 className="card-title">🔴 Total Pending — Breakdown</h2>
            <BreakdownCards rows={rows} total={total} />
          </div>
        );
      })()}

      {/* ── Total Fee Billed Breakdown ── */}
      {showBilled && (() => {
        const tuition = parseFloat(stats.billed_tuition || 0);
        const bus     = parseFloat(stats.billed_bus     || 0);
        const uniform = parseFloat(stats.billed_uniform || 0);
        const books   = parseFloat(stats.billed_books   || 0);
        const hostel  = parseFloat(stats.billed_hostel  || 0);
        const total   = parseFloat(stats.total_fees     || 0);
        const rows = [
          { label: '📚 Tuition',  amount: tuition, color: '#4c1d95', bg: '#ede9fe' },
          { label: '🚌 Bus',      amount: bus,     color: '#065f46', bg: '#d1fae5' },
          { label: '👕 Uniform',  amount: uniform,  color: '#1e40af', bg: '#dbeafe' },
          { label: '📖 Books',    amount: books,    color: '#92400e', bg: '#fef3c7' },
          { label: '🏠 Hostel',   amount: hostel,  color: '#0f766e', bg: '#ccfbf1' },
        ].filter((r) => r.amount > 0);
        return (
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 className="card-title">📋 Total Fee Billed — Breakdown</h2>
            <BreakdownCards rows={rows} total={total} />
          </div>
        );
      })()}

      {/* ── Today's Collection Breakdown ── */}
      {showToday && (() => {
        const tuition = parseFloat(stats.today_tuition || 0);
        const bus     = parseFloat(stats.today_bus     || 0);
        const uniform = parseFloat(stats.today_uniform || 0);
        const books   = parseFloat(stats.today_books   || 0);
        const hostel  = parseFloat(stats.today_hostel  || 0);
        const oldDue  = parseFloat(stats.today_old_due || 0);
        const other   = parseFloat(stats.today_other   || 0);
        const total   = parseFloat(stats.today_collected || 0);
        const rows = [
          { label: '📚 Tuition',    amount: tuition, color: '#1e40af', bg: '#dbeafe' },
          { label: '🚌 Bus',        amount: bus,     color: '#065f46', bg: '#d1fae5' },
          { label: '👕 Uniform',    amount: uniform,  color: '#7c3aed', bg: '#ede9fe' },
          { label: '📖 Books',      amount: books,    color: '#92400e', bg: '#fef3c7' },
          { label: '🏠 Hostel',     amount: hostel,  color: '#0f766e', bg: '#ccfbf1' },
          { label: '💸 Old Due',    amount: oldDue,  color: '#b91c1c', bg: '#fee2e2' },
          { label: '🔖 Other',      amount: other,   color: '#374151', bg: '#f3f4f6' },
        ].filter((r) => r.amount > 0);
        return (
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 className="card-title">📅 Today's Collection — Breakdown</h2>
            <BreakdownCards rows={rows} total={total} />
          </div>
        );
      })()}

      {/* ── Archived Students List ── */}
      {showArchivedList && (() => {
        const list = archivedStudents.filter((s) => s.status === 'archived');
        return (
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 className="card-title">🗃️ Archived Students ({list.length})</h2>
            {list.length === 0 ? <p className="no-data">No archived students.</p> : (
              <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Class</th><th>Student Name</th><th>Father Name</th><th>Village</th><th>Year</th></tr></thead>
                <tbody>
                  {list.map((s) => (
                    <tr key={s.student_id}>
                      <td><span className="class-badge">{s.class}</span></td>
                      <td><strong>{s.student_name}</strong></td>
                      <td style={{ color: '#64748b' }}>{s.father_name}</td>
                      <td>{s.village}</td>
                      <td style={{ color: '#64748b' }}>{s.academic_year}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── TC Issued List ── */}
      {showTCList && (() => {
        const list = archivedStudents.filter((s) => s.status === 'tc_issued');
        return (
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 className="card-title">📄 TC Issued Students ({list.length})</h2>
            {list.length === 0 ? <p className="no-data">No TC issued yet.</p> : (
              <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Class</th><th>Student Name</th><th>Father Name</th><th>Village</th><th>TC Date</th><th>Year</th></tr></thead>
                <tbody>
                  {list.map((s) => (
                    <tr key={s.student_id}>
                      <td><span className="class-badge">{s.class}</span></td>
                      <td><strong>{s.student_name}</strong></td>
                      <td style={{ color: '#64748b' }}>{s.father_name}</td>
                      <td>{s.village}</td>
                      <td style={{ color: '#64748b' }}>{s.tc_date ? new Date(s.tc_date).toLocaleDateString('en-IN') : '—'}</td>
                      <td style={{ color: '#64748b' }}>{s.academic_year}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Due Students List ── */}
      {showDueList && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h2 className="card-title">⏳ Students With Dues ({pendingList.length})</h2>
          <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Class</th>
                <th>Student Name</th>
                <th>Father Name</th>
                <th>Village</th>
                <th>Pending</th>
              </tr>
            </thead>
            <tbody>
              {pendingList.map((s) => (
                <tr key={s.student_id}>
                  <td><span className="class-badge">{s.class}</span></td>
                  <td><strong>{s.student_name}</strong></td>
                  <td style={{ color: '#64748b' }}>{s.father_name}</td>
                  <td>{s.village}</td>
                  <td><span className="pending-chip due">{fmt(s.pending_amount)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* ── Old Due Students List ── */}
      {showOldDueList && (() => {
        const oldDueStudents = pendingList.filter((s) => parseFloat(s.old_due_amount) > 0);
        return (
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 className="card-title">💸 Students With Old Due — Prev. Years ({oldDueStudents.length})</h2>
            <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Student Name</th>
                  <th>Father Name</th>
                  <th>Village</th>
                  <th>Old Due</th>
                  <th>Total Pending</th>
                </tr>
              </thead>
              <tbody>
                {oldDueStudents.map((s) => (
                  <tr key={s.student_id}>
                    <td><span className="class-badge">{s.class}</span></td>
                    <td><strong>{s.student_name}</strong></td>
                    <td style={{ color: '#64748b' }}>{s.father_name}</td>
                    <td>{s.village}</td>
                    <td><span className="pending-chip due">{fmt(s.old_due_amount)}</span></td>
                    <td><span className="pending-chip due">{fmt(s.pending_amount)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        );
      })()}

      {/* ── Gender / Strength Breakdown Panel ── */}
      {showStrength && strength && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h2 className="card-title">Student Strength — Gender Breakdown</h2>

          {/* Overall summary */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            {[
              { label: 'Total',    value: strength.overall.total,       bg: '#e3f2fd', color: '#1565c0' },
              { label: '👦 Male',  value: strength.overall.male,        bg: '#e8f5e9', color: '#2e7d32' },
              { label: '👧 Female',value: strength.overall.female,      bg: '#fce4ec', color: '#880e4f' },
              { label: '— Not set',value: strength.overall.unspecified, bg: '#f5f5f5', color: '#616161' },
            ].map((item) => (
              <div key={item.label} style={{ background: item.bg, borderRadius: 10, padding: '14px 24px', minWidth: 110, textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: item.color }}>{item.value}</div>
                <div style={{ fontSize: 13, color: item.color, marginTop: 2 }}>{item.label}</div>
              </div>
            ))}
          </div>

          {/* Class-wise table */}
          <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Class</th>
                <th style={{ textAlign: 'center' }}>Total</th>
                <th style={{ textAlign: 'center' }}>👦 Male</th>
                <th style={{ textAlign: 'center' }}>👧 Female</th>
                <th style={{ textAlign: 'center' }}>— Not set</th>
              </tr>
            </thead>
            <tbody>
              {strength.classwise.map((row) => (
                <tr key={row.class}>
                  <td><span className="badge">{row.class}</span></td>
                  <td style={{ textAlign: 'center' }}><strong>{row.total}</strong></td>
                  <td style={{ textAlign: 'center', color: '#1565c0' }}>{row.male}</td>
                  <td style={{ textAlign: 'center', color: '#880e4f' }}>{row.female}</td>
                  <td style={{ textAlign: 'center', color: '#9e9e9e' }}>{row.unspecified}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: '#f5f5f5' }}>
                <td>Total</td>
                <td style={{ textAlign: 'center' }}>{strength.overall.total}</td>
                <td style={{ textAlign: 'center', color: '#1565c0' }}>{strength.overall.male}</td>
                <td style={{ textAlign: 'center', color: '#880e4f' }}>{strength.overall.female}</td>
                <td style={{ textAlign: 'center', color: '#9e9e9e' }}>{strength.overall.unspecified}</td>
              </tr>
            </tfoot>
          </table>
          </div>
        </div>
      )}

      {/* ── Analytics Row: Charts ── */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {/* Collection Rate Pie */}
          <div className="card" style={{ padding: '1.25rem' }}>
            <h2 className="card-title" style={{ marginBottom: '1rem' }}>Collection Rate</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <div style={{ width: 110, height: 110, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={buildPieData(stats)}
                      cx="50%" cy="50%"
                      innerRadius={30} outerRadius={50}
                      dataKey="value" startAngle={90} endAngle={-270}
                    >
                      {buildPieData(stats).map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-1px' }}>{collectionRate}%</div>
                <div style={{ fontSize: '.75rem', color: '#64748b', marginBottom: '1rem' }}>of total fees collected</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.78rem' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ color: '#475569' }}>Collected</span>
                    <span style={{ fontWeight: 700, color: '#15803d', marginLeft: 'auto' }}>{fmt(stats.total_collected)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.78rem' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f97316', display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ color: '#475569' }}>Pending</span>
                    <span style={{ fontWeight: 700, color: '#c2410c', marginLeft: 'auto' }}>{fmt(stats.total_pending)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.78rem' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6366f1', display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ color: '#475569' }}>Total Billed</span>
                    <span style={{ fontWeight: 700, color: '#4338ca', marginLeft: 'auto' }}>{fmt(stats.total_fees)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Fee Type Bar Chart */}
          {buildChartData(stats).length > 0 && (
            <div className="card" style={{ padding: '1.25rem' }}>
              <h2 className="card-title" style={{ marginBottom: '1rem' }}>Fee Type Breakdown</h2>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={buildChartData(stats)} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} barSize={14}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="collected" name="Collected" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pending"   name="Pending"   fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', gap: '1.5rem', marginTop: '.75rem' }}>
                <span style={{ fontSize: '.72rem', color: '#22c55e', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: '#22c55e', display: 'inline-block' }} /> Collected
                </span>
                <span style={{ fontSize: '.72rem', color: '#f97316', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: '#f97316', display: 'inline-block' }} /> Pending
                </span>
              </div>
            </div>
          )}

          {/* Today Summary */}
          <div className="card" style={{ padding: '1.25rem' }}>
            <h2 className="card-title" style={{ marginBottom: '1rem' }}>📅 Today's Summary</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
              {[
                { label: "Today's Collection", value: fmt(stats.today_collected), color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
                { label: "Today's Tuition",    value: fmt(stats.today_tuition),   color: '#1e40af', bg: '#eff6ff', border: '#bfdbfe' },
                { label: "Today's Bus",        value: fmt(stats.today_bus),       color: '#065f46', bg: '#f0fdf4', border: '#a7f3d0' },
                { label: "Today's Other",      value: fmt((stats.today_uniform || 0) + (stats.today_books || 0) + (stats.today_other || 0)), color: '#7c3aed', bg: '#faf5ff', border: '#e9d5ff' },
              ].map((item) => (
                <div key={item.label} style={{ background: item.bg, border: `1px solid ${item.border}`, borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ fontSize: '.68rem', color: item.color, fontWeight: 600, marginBottom: 3 }}>{item.label}</div>
                  <div style={{ fontSize: '.95rem', fontWeight: 800, color: item.color }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Quick Actions ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 className="section-title">Quick Actions</h2>
        <div className="quick-actions-grid">
          {[
            { label: 'Add Student',      icon: '👨‍🎓', href: '/students' },
            { label: 'Collect Fee',      icon: '💰', href: '/students' },
            { label: 'Day Book',         icon: '📒', href: '/daybook' },
            { label: 'Accounts',         icon: '📈', href: '/reports' },
            { label: 'Exams',            icon: '📝', href: '/exams' },
            { label: 'Operations',       icon: '⚙️', href: '/operations' },
          ].map((a) => (
            <a key={a.label} href={a.href} className="quick-action-btn">
              <div className="quick-action-icon">{a.icon}</div>
              <span>{a.label}</span>
            </a>
          ))}
        </div>
      </div>

      {/* Top Defaulters */}
      {topDefaulters.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '.5rem' }}>
            <h2 className="section-title" style={{ marginBottom: 0 }}>🔴 Top Fee Defaulters</h2>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowDueList((v) => !v)}>
              {showDueList ? 'Hide' : 'View All'} ({pendingList.length})
            </button>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th><th>Student</th><th>Class</th><th>Village</th><th>Pending</th>
                </tr>
              </thead>
              <tbody>
                {topDefaulters.map((s, i) => (
                  <tr key={s.student_id}>
                    <td className="id-cell">{i + 1}</td>
                    <td>
                      <strong>{s.student_name}</strong>
                      {s.father_name && <div style={{ fontSize: '.75rem', color: '#94a3b8' }}>{s.father_name}</div>}
                    </td>
                    <td><span className="class-badge">{s.class}</span></td>
                    <td style={{ color: '#64748b' }}>{s.village}</td>
                    <td><span className="pending-chip due">{fmt(s.pending_amount)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Parent App Link */}
      <ParentAppCard />
    </div>
  );
}

function ParentAppCard() {
  const schoolCode = sessionStorage.getItem('gs_school_code') || '';
  const schoolName = sessionStorage.getItem('gs_school_name') || '';
  const [copied, setCopied] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  if (!schoolCode || schoolCode === '__admin__') return null;

  const parentUrl = `${window.location.origin}/parent/login?school=${encodeURIComponent(schoolCode)}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(parentUrl)}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(parentUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownloadQR = async () => {
    setDownloading(true);
    try {
      const res = await fetch(qrUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `parent-portal-qr-${schoolCode}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download QR code. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <h2 className="card-title">📱 Parent App</h2>
      <p style={{ color: '#475569', marginBottom: 16, lineHeight: 1.6 }}>
        Share this link or QR code with parents. They can enter their mobile number to view their child's attendance, marks, homework and fee details — without logging in.
      </p>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 260px' }}>
          <div style={{
            background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 12,
            padding: '12px 16px', fontFamily: 'monospace', fontSize: 14,
            color: '#0f172a', wordBreak: 'break-all', marginBottom: 12,
          }}>
            {parentUrl}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={handleCopy}
              style={{
                padding: '10px 20px', background: copied ? '#15803d' : '#1d4ed8',
                color: '#fff', border: 'none', borderRadius: 10,
                fontWeight: 700, cursor: 'pointer', fontSize: 14, transition: 'background 0.2s',
              }}
            >
              {copied ? '✅ Copied!' : '📋 Copy Link'}
            </button>
            <a
              href={parentUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '10px 20px', background: '#f1f5f9', color: '#0f172a',
                border: '1px solid #e2e8f0', borderRadius: 10,
                fontWeight: 700, fontSize: 14, textDecoration: 'none', display: 'inline-block',
              }}
            >
              🔗 Open
            </a>
          </div>
          <p style={{ marginTop: 12, color: '#64748b', fontSize: 13 }}>
            💡 Share the QR code in school notices or WhatsApp groups so parents can scan and access directly.
          </p>
        </div>
        <div style={{ textAlign: 'center' }}>
          <img
            src={qrUrl}
            alt="Parent App QR Code"
            style={{ width: 160, height: 160, borderRadius: 12, border: '1px solid #e2e8f0' }}
          />
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>Scan to open Parent App</div>
          <button
            onClick={handleDownloadQR}
            disabled={downloading}
            style={{
              marginTop: 10, padding: '8px 18px', background: downloading ? '#94a3b8' : '#0f172a',
              color: '#fff', border: 'none', borderRadius: 10,
              fontWeight: 700, cursor: downloading ? 'not-allowed' : 'pointer', fontSize: 13,
              transition: 'background 0.2s',
            }}
          >
            {downloading ? '⏳ Downloading...' : '⬇️ Download QR'}
          </button>
        </div>
      </div>
    </div>
  );
}
